import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronLeft, ChevronRight, Clock, Copy, MessagesSquare, Pin, PinOff } from 'lucide-react'

import { api } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDropdownOptions } from '@/hooks/useDropdownOptions'
import {
  CONVERSATION_STATUSES,
  type CampaignListResponse,
  type ConversationListItem,
  type ConversationListResponse,
  type ConversationStatus,
  type SdrListResponse,
  type SellerListResponse,
} from '@sdr-agend/admin-schemas'

/**
 * Status → BR-pt label + cor própria. Keeping a static map here avoids
 * importing a sprawling i18n bundle for a single page.
 *
 * 2026-07-24 (user request) — antes, os 17 status (+ o "Liberado (IA ativa)"
 * derivado) se dividiam em só 6 cores de Badge (`success`/`warning`/
 * `destructive`/`secondary`/`default`/`outline`), com até 6 status diferentes
 * (todos os "aguardando_*" + handoff) caindo no MESMO amarelo — impossível
 * distinguir qual status era qual só pela cor. Cada status agora tem sua
 * própria família de cor Tailwind (18 famílias distintas, nunca repetidas),
 * agrupadas por semântica (positivo=verdes, negativo/crítico=vermelhos,
 * neutro/encerrado=cinzas, em andamento=cores quentes/frias variadas). A cor
 * nunca é o único sinal — o texto do label sempre acompanha o badge.
 */
const STATUS_META: Record<ConversationStatus, { label: string; className: string }> = {
  scheduled: { label: 'Agendada', className: 'border-transparent bg-indigo-500 text-white hover:bg-indigo-500/80' },
  awaiting_response: {
    label: 'Aguardando resposta',
    className: 'border-transparent bg-amber-500 text-white hover:bg-amber-500/80',
  },
  awaiting_qualification: {
    label: 'Aguardando qualificação',
    className: 'border-transparent bg-yellow-500 text-white hover:bg-yellow-500/80',
  },
  // Story 5.3 — set after the qualification handler enqueues a scheduling job;
  // Story 5.5 owns the transition out of this state when slots are offered.
  awaiting_slots: {
    label: 'Aguardando horários',
    className: 'border-transparent bg-lime-500 text-white hover:bg-lime-500/80',
  },
  // Story 5.5 — set after lead-scheduling-handler offers 3 slots; waits for
  // customer to pick one (1/2/3 or free-text).
  awaiting_slot_choice: {
    label: 'Aguardando escolha de horário',
    className: 'border-transparent bg-teal-500 text-white hover:bg-teal-500/80',
  },
  // Story 5.5 — set when SellerDistributor returns null (no slots in 7d
  // window). Manager picks up the conversation manually via /admin/conversations.
  awaiting_human_followup: {
    label: 'Aguardando follow-up humano',
    className: 'border-transparent bg-fuchsia-500 text-white hover:bg-fuchsia-500/80',
  },
  active: { label: 'Ativa', className: 'border-transparent bg-blue-600 text-white hover:bg-blue-600/80' },
  confirmed: {
    label: 'Confirmada',
    className: 'border-transparent bg-emerald-500 text-white hover:bg-emerald-500/80',
  },
  cancelled: {
    label: 'Cancelada',
    className: 'border-transparent bg-rose-500 text-white hover:bg-rose-500/80',
  },
  rescheduled: {
    label: 'Reagendada',
    className: 'border-transparent bg-violet-500 text-white hover:bg-violet-500/80',
  },
  human_handoff: {
    label: 'Handoff humano',
    className: 'border-transparent bg-orange-600 text-white hover:bg-orange-600/80',
  },
  cold: { label: 'Frio', className: 'border-transparent bg-slate-400 text-white hover:bg-slate-400/80' },
  declined: {
    label: 'Recusada',
    className: 'border-transparent bg-red-600 text-white hover:bg-red-600/80',
  },
  attended: {
    label: 'Atendida',
    className: 'border-transparent bg-green-600 text-white hover:bg-green-600/80',
  },
  no_show: {
    label: 'No-show',
    className: 'border-transparent bg-pink-600 text-white hover:bg-pink-600/80',
  },
  closed: { label: 'Encerrada', className: 'border-transparent bg-gray-500 text-white hover:bg-gray-500/80' },
  error: { label: 'Erro', className: 'border-transparent bg-stone-700 text-white hover:bg-stone-700/80' },
}

/**
 * 2026-07-21 — `status` intentionally stays `human_handoff` after a release
 * (Story 6.1 Decision #4: the next inbound message lets the intent router
 * transition it naturally). `HandoffAlertBanner`'s count already accounts
 * for this by filtering on `agentSilenced`, but this table's Status column
 * used to read `status` directly and kept showing "Handoff humano" for
 * conversations an operator had already released — this derives the badge
 * from BOTH fields so a released conversation shows a distinct, non-alarming
 * label instead.
 */
function getStatusMeta(
  c: ConversationListItem,
): (typeof STATUS_META)[ConversationStatus] {
  if (c.status === 'human_handoff' && !c.agentSilenced) {
    return {
      label: 'Liberado (IA ativa)',
      className: 'border-transparent bg-sky-500 text-white hover:bg-sky-500/80',
    }
  }
  return STATUS_META[c.status]
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number]

function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR')
}

/**
 * Story 9.7 — "recontato agendado" indicator. Visually distinct from the
 * status badge (own icon + always `secondary` regardless of conversation
 * status) so it's never confused with "Handoff humano" or a generic stalled
 * status. Renders nothing when there's no pending callback — the indicator
 * is derived live from `scheduled_dispatches` on every read (see
 * `pendingCallbackAt` in `packages/admin-schemas/src/conversations.ts`), so
 * it disappears the moment the callback is cancelled or fires — no stale
 * badge risk like the Story 8.3 handoff bug.
 */
function PendingCallbackBadge({ pendingCallbackAt }: { pendingCallbackAt: string | null }): React.ReactElement | null {
  if (pendingCallbackAt === null) return null
  return (
    <Badge variant="secondary" className="inline-flex items-center gap-1">
      <Clock className="h-3 w-3" aria-hidden="true" />
      Recontato: {formatDateTime(pendingCallbackAt)}
    </Badge>
  )
}

/**
 * Story 10.1 (2026-07-28, user request) — truncated conversation ID + copy
 * button, so an operator can reference a specific conversation precisely
 * when reporting an issue (today they can only describe "the conversation
 * with customer X"). `stopPropagation` on the click is required — the whole
 * table row navigates on click.
 */
function CopyConversationId({ id }: { id: string }): React.ReactElement {
  const [copied, setCopied] = useState(false)

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded px-1 font-mono text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
      onClick={(e) => {
        e.stopPropagation()
        void navigator.clipboard.writeText(id).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      aria-label={`Copiar ID da conversa ${id}`}
      title={id}
    >
      {copied ? (
        <Check className="h-3 w-3 text-emerald-600" aria-hidden="true" />
      ) : (
        <Copy className="h-3 w-3" aria-hidden="true" />
      )}
      {id.slice(0, 8)}
    </button>
  )
}

/**
 * Story 10.2 (2026-07-28, user request) — pin/unpin so an operator can keep
 * an important conversation at the top of the list. Backend already exists
 * (`PATCH /admin/conversations/:id/pin`, `pinnedAt`-first ordering) — this is
 * only the button + optimistic-ish refetch. `stopPropagation` for the same
 * reason as `CopyConversationId` above.
 */
function PinButton({
  conversationId,
  pinned,
}: {
  conversationId: string
  pinned: boolean
}): React.ReactElement {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (nextPinned: boolean) =>
      api(`/admin/conversations/${conversationId}/pin`, {
        method: 'PATCH',
        body: JSON.stringify({ pinned: nextPinned }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-conversations'] })
    },
  })

  return (
    <button
      type="button"
      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
      onClick={(e) => {
        e.stopPropagation()
        mutation.mutate(!pinned)
      }}
      disabled={mutation.isPending}
      aria-label={pinned ? 'Desafixar conversa' : 'Fixar conversa'}
      aria-pressed={pinned}
      title={pinned ? 'Desafixar conversa' : 'Fixar conversa'}
    >
      {pinned ? (
        <Pin className="h-4 w-4 fill-current text-amber-500" aria-hidden="true" />
      ) : (
        <PinOff className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  )
}

/**
 * `/admin/conversations` — observability list (Story 3.10).
 *
 * Filters: status, sellerId, dateFrom/dateTo, search, customerPhone.
 * Pagination: page + perPage; server clamps perPage at 100.
 *
 * RBAC: every authenticated role can read. The route handler enforces auth;
 * we don't gate UI elements here because there are no mutations on this page.
 */
/** Validates the `?status=` deep-link param (e.g. from `HandoffAlertBanner`) against the known enum — an unrecognized value falls back to "todos os status" instead of silently 400ing the list query. */
function initialStatusFromSearchParams(params: URLSearchParams): ConversationStatus | '' {
  const raw = params.get('status')
  return CONVERSATION_STATUSES.includes(raw as ConversationStatus)
    ? (raw as ConversationStatus)
    : ''
}

export function ConversationsPage(): React.ReactElement {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | ''>(() =>
    initialStatusFromSearchParams(searchParams),
  )
  // The lazy initializer above only runs on mount — it misses a `?status=`
  // deep-link (e.g. `HandoffAlertBanner`) navigated to while this page is
  // ALREADY mounted (React Router reuses the element across same-route
  // navigations that only change the query string). This effect re-syncs
  // whenever the URL's `status` param changes to a recognized value.
  const statusParam = searchParams.get('status')
  useEffect(() => {
    if (CONVERSATION_STATUSES.includes(statusParam as ConversationStatus)) {
      setStatusFilter(statusParam as ConversationStatus)
    }
  }, [statusParam])
  const [sellerId, setSellerId] = useState('')
  // Story 12.8 (Epic E12) — admin/manager can optionally filter by carteira
  // (backend capability already shipped in Story 12.4); a real sdr session
  // has this forced server-side regardless of what's typed here.
  const [sdrId, setSdrId] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<PageSize>(25)

  // Story 10.3 (2026-07-28, user request) — same query key as Dashboard.tsx's
  // campaign dropdown so both pages share the one cache entry.
  const { options: campaignOptions, isLoading: campaignsLoading } = useDropdownOptions<
    CampaignListResponse,
    CampaignListResponse['campaigns'][number]
  >({
    queryKey: ['admin-campaigns', { typeFilter: '', activeFilter: '' }],
    url: '/admin/campaigns',
    selectItems: (r) => r.campaigns,
    mapOption: (c) => ({ id: c.id, name: c.name }),
  })

  // 2026-09-24 (user request) — "Vendedor"/"Carteira SDR" were free-text UUID
  // inputs; now proper dropdowns, same pattern as the campaign one above.
  // Same query key as Dashboard.tsx's seller dropdown (`sellers-active-dropdown`)
  // — shares the cache entry across pages, mirroring the campaign dropdown's
  // own cache-sharing rationale.
  const { options: sellerOptions, isLoading: sellersLoading } = useDropdownOptions<
    SellerListResponse,
    SellerListResponse['sellers'][number]
  >({
    queryKey: ['sellers-active-dropdown'],
    url: '/admin/sellers?active=true',
    selectItems: (r) => r.sellers,
    mapOption: (s) => ({ id: s.id, name: s.name }),
  })

  // `GET /admin/sdrs` (new, 2026-09-24) — open to any authenticated role
  // (mirrors `GET /admin/sellers`), unlike `GET /admin/dashboard/sdr-comparison`
  // (admin/manager only, used for the Dashboard's SDR filter) — this page is
  // visible to every role, so the dropdown source can't be RBAC-gated.
  const { options: sdrOptions, isLoading: sdrsLoading } = useDropdownOptions<
    SdrListResponse,
    SdrListResponse['sdrs'][number]
  >({
    queryKey: ['sdrs-dropdown'],
    url: '/admin/sdrs',
    selectItems: (r) => r.sdrs,
    mapOption: (s) => ({ id: s.id, name: s.name }),
  })

  const queryKey = useMemo(
    () => [
      'admin-conversations',
      { statusFilter, sellerId, sdrId, campaignId, dateFrom, dateTo, search, page, perPage },
    ],
    [statusFilter, sellerId, sdrId, campaignId, dateFrom, dateTo, search, page, perPage],
  )

  const { data, isLoading, isError } = useQuery<ConversationListResponse>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter !== '') params.set('status', statusFilter)
      if (sellerId.trim() !== '') params.set('sellerId', sellerId.trim())
      if (sdrId.trim() !== '') params.set('sdrId', sdrId.trim())
      if (campaignId !== '') params.set('campaignId', campaignId)
      if (dateFrom !== '') {
        // <input type="datetime-local"> emits 'YYYY-MM-DDTHH:mm' (no TZ);
        // append :00Z so the backend's date-time format validator accepts it.
        params.set('dateFrom', `${dateFrom}:00Z`)
      }
      if (dateTo !== '') params.set('dateTo', `${dateTo}:00Z`)
      if (search.trim() !== '') params.set('search', search.trim())
      params.set('page', String(page))
      params.set('perPage', String(perPage))
      return api<ConversationListResponse>(`/admin/conversations?${params.toString()}`)
    },
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / perPage))
  const canPrev = page > 1
  const canNext = page < totalPages

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Conversas</h1>
          <p className="text-sm text-muted-foreground">
            Acompanhe conversas em andamento, intenções detectadas e status de confirmação.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-1">
          <label htmlFor="filter-status" className="text-xs font-medium text-muted-foreground">
            Status
          </label>
          <Select
            id="filter-status"
            aria-label="Filtrar por status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ConversationStatus | '')
              setPage(1)
            }}
          >
            <option value="">Todos os status</option>
            {CONVERSATION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_META[s].label}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <label htmlFor="filter-campaign" className="text-xs font-medium text-muted-foreground">
            Campanha
          </label>
          <Select
            id="filter-campaign"
            aria-label="Filtrar por campanha"
            value={campaignId}
            disabled={campaignsLoading}
            onChange={(e) => {
              setCampaignId(e.target.value)
              setPage(1)
            }}
          >
            <option value="">Todas as campanhas</option>
            {campaignOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <label htmlFor="filter-seller" className="text-xs font-medium text-muted-foreground">
            Vendedor
          </label>
          <Select
            id="filter-seller"
            value={sellerId}
            onChange={(e) => {
              setSellerId(e.target.value)
              setPage(1)
            }}
            disabled={sellersLoading}
            aria-label="Filtrar por vendedor"
            data-testid="filter-seller-select"
          >
            <option value="">Todos os vendedores</option>
            {sellerOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <label htmlFor="filter-sdr" className="text-xs font-medium text-muted-foreground">
            Carteira SDR
          </label>
          <Select
            id="filter-sdr"
            value={sdrId}
            onChange={(e) => {
              setSdrId(e.target.value)
              setPage(1)
            }}
            disabled={sdrsLoading}
            aria-label="Filtrar por carteira de SDR"
            data-testid="filter-sdr-select"
          >
            <option value="">Todas as SDRs</option>
            {sdrOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <label htmlFor="filter-from" className="text-xs font-medium text-muted-foreground">
            De
          </label>
          <Input
            id="filter-from"
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value)
              setPage(1)
            }}
            aria-label="Data inicial"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="filter-to" className="text-xs font-medium text-muted-foreground">
            Até
          </label>
          <Input
            id="filter-to"
            type="datetime-local"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value)
              setPage(1)
            }}
            aria-label="Data final"
          />
        </div>
        <div className="space-y-1 md:col-span-2 lg:col-span-5">
          <label htmlFor="filter-search" className="text-xs font-medium text-muted-foreground">
            Buscar (cliente / telefone)
          </label>
          <Input
            id="filter-search"
            placeholder="Nome ou trecho do telefone…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            aria-label="Buscar conversas"
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" aria-label="Fixar" />
              <TableHead>Cliente</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Campanha</TableHead>
              <TableHead>Vendedor</TableHead>
              <TableHead>SDR</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Agendada</TableHead>
              <TableHead>Última mensagem</TableHead>
              <TableHead>Última intenção</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-sm text-destructive">
                  Erro ao carregar conversas.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-sm text-muted-foreground">
                  Nenhuma conversa encontrada.
                </TableCell>
              </TableRow>
            )}
            {items.map((c: ConversationListItem) => {
              const meta = getStatusMeta(c)
              return (
                <TableRow
                  key={c.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/admin/conversations/${c.id}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/admin/conversations/${c.id}`)
                  }}
                  aria-label={`Abrir conversa de ${c.customerName ?? c.customerPhone}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <PinButton conversationId={c.id} pinned={c.pinnedAt !== null} />
                  </TableCell>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2">
                      <MessagesSquare
                        className="h-4 w-4 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <span className="flex flex-col">
                        {c.customerName ?? '—'}
                        <CopyConversationId id={c.id} />
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{c.customerPhone}</TableCell>
                  <TableCell>
                    {c.campaignName ? (
                      <Badge variant="outline">{c.campaignName}</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>{c.sellerName ?? '—'}</TableCell>
                  <TableCell>{c.sdrName ?? '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <Badge className={meta.className}>{meta.label}</Badge>
                      <PendingCallbackBadge pendingCallbackAt={c.pendingCallbackAt} />
                    </div>
                  </TableCell>
                  <TableCell>{formatDateTime(c.scheduledAt)}</TableCell>
                  <TableCell>{formatDateTime(c.lastMessageAt)}</TableCell>
                  <TableCell>
                    {c.lastInboundIntent !== null ? (
                      <span className="rounded-md border px-2 py-0.5 text-xs uppercase tracking-wide">
                        {c.lastInboundIntent}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>
            {total === 0
              ? '0 conversas'
              : `Página ${data?.page ?? page} de ${totalPages} — ${total} conversas`}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="filter-perPage" className="text-xs text-muted-foreground">
            Itens por página
          </label>
          <Select
            id="filter-perPage"
            aria-label="Itens por página"
            value={String(perPage)}
            onChange={(e) => {
              const next = Number.parseInt(e.target.value, 10) as PageSize
              setPerPage(next)
              setPage(1)
            }}
            className="w-24"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Anterior
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canNext}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Próxima página"
          >
            Próxima
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ConversationsPage
